import { Injectable, Injector, OnDestroy } from '@angular/core';

import { Subscription, BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import _ from 'underscore';
import { uniqBy, isNil } from 'lodash';

import { FilterService } from '../../Filter/services/abstract-filter.service';
import { FilterTypeCheckboxModel } from '../../Filter/models/filter-type-checkbox.model';
import { FilterTypeTextModel } from '../../Filter/models/filter-type-text.model';
import { FilterTypeRangeModel } from '../../Filter/models/filter-type-range.model';
import { MentionSearchCriteriaModel } from '../../../Monitor/Shared/models/mention-search-criteria.model';
import { FilterOptionModel } from '../../Filter/models/filter-option.model';
import { JournodexSourceModel } from '../../Journodex/models/journodex-source.model';
import { SearchCriteriaSourcesEnum } from '../../../api/mentions/enum/search-criteria-sources.enum';
import { SearchCriteriaQuerySentimentsEnum } from '../../../api/mentions/enum/search-criteria-query-sentiments.enum';
import { SentimentModel } from '../../../Monitor/Shared/models/sentiment.model';
import { MediaTypeModel } from '../../../Monitor/Shared/models/media-type.model';
import { TagsService } from '../../Tags/services/tags.service';
import { isoLanguageNames } from '../../Forms/data/iso-language-names';
import { JournodexGeographyModel } from '../../Journodex/models/journodex-geography.model';
import { LanguageModel } from '../../../Core/Localization/models/language.model';
import { monitoringFiltersEnum } from '../../../Monitor/Shared/enums/monitoring-filters.enum';
import { Mention } from '../models/mention';
import { JournodexRegionsService } from '../../Journodex/services/journodex-regions.service';
import { FilterTypeAutocompleteModel } from '../../Filter/models/filter-type-autocomplete.model';
import { MonitoringKeywordFilterComponent } from '../../../Monitor/Shared/components/KeywordFilter/filter-keyword.component';
import { KeywordFilterModeEnum, MonitoringKeywordFilterModel } from '../../../Monitor/Shared/models/monitoring-keyword-filter.model';
import { FilterTypeDuplicatesModel } from '../../Filter/models/filter-type-duplicates.model';
import { TagGroupsService } from '../../Tags/services/tag-groups.service';
import { DuplicatesStatusEnum } from '../../Filter/enums/duplicates-status.enum';
import { SourceGroupService } from '../../Sources/services/source-group.service';
import { LocalizationService } from '../../../Core/Localization/services/localization.service';
import { engagementFilterKeys, EngagementFilterModel } from '../../Filter/models/engagement-filter.model';
import { EngagementFilterComponent } from '../../Filter/components/EngagementFilter/engagement-filter.component';
import { ProminenceFilterModel } from '../../../Monitor/Shared/components/ProminenceFilter/prominence-filter.model';
import { ProminenceFilterComponent } from '../../../Monitor/Shared/components/ProminenceFilter/prominence-filter.component';
import { AccountPreferencesService } from '../../../Account/AccountPreferences/services/account-preferences.service';
import { MonitoringSeriesTypeEnum } from '../../../Monitor/Shared/enums/monitoring-series-type.enum';
import { MediaTypeEnum } from '@Common/Basic/enums/media-type.enum';
import { LinkedinSubTypeEnum } from '../../Basic/enums/linkedin-sub-type.enum';
import { mediaTypesWithSubTypesMap, MediaTypeTreeFilterModel } from '../../Filter/models/media-type-tree-filter.model';
import { MediaTypeTreeFilterComponent } from '../../Filter/components/MediaTypesTreeFilter/media-type-tree-filter.component';
import { SelectedMediaTypesFilterModel } from '../../../Monitor/Shared/models/selected-media-types-filter.model';
import { MonitoringAdvancedBooleanModel } from '../../../Monitor/Shared/models/monitoring-advanced-boolean.model';
import { DateQueryModel } from '../../Forms/models/date-query.model';
import { FilterSectionEnum } from '../../Filter/enums/filter-section.enum';
import { SearchMode } from '../../../Monitor/Shared/enums/search-mode.enum';
import { CurrentUserStateService } from '../../../Core/Users/services/current-user-state.service';
import { TwitterSubTypeEnum } from '@Common/Basic/enums/twitter-sub-type.enum';
import { IncludeExcludeEnum } from '../../Filter/enums/include-exclude.enum';
import { MentionSearchQueryModel } from '../../../Monitor/Shared/models/mention-search-query.model';
import { SavedFilter } from '../../Filter/models/saved-filter.model';
import { SavedFilterTypeEnum } from '../../Filter/enums/saved-filter.enum';
import { ProminenceFilterApiModel } from '../../../api/mentions/model/prominence-filter-api.model';
import { SavedFilterCard, SavedFilterDisplayModel } from '@Common/Filter/components/SavedFilterWrapper/saved-filter-type.model';
import { SourceTypeEnum } from '@api/source/enums/source-type.enum';
import { ChipModel } from '@Common/Forms/models/chip.model';
import { SavedFilterService } from '@Common/Filter/services/saved-filter.service';
import { CurrencyConversionService } from '@Core/Users/services/currency-conversion.service';
import { DiscoverListsFilterComponent } from '../../Filter/components/DiscoverListsFilter/discover-lists-filter.component';
import { DiscoverListsFilterModel } from '../../Filter/models/discover-lists-filter.model';
import { FilterTypeModel } from '../../Filter/models/abstract-filter-type.model';
import { DiscoverListsFilterService } from '../../Filter/services/discover-lists-filter.service';
import { BlueskySubTypeEnum } from '@Common/Basic/enums/bluesky-sub-type.enum';
import { BroadcastSubTypeEnum } from '../../../Common/Basic/enums/broadcast-sub-type.enum';
import { AgentsFilterModel } from '../../../Monitor/Shared/components/AgentsFilter/agents-filter.model';
import { AgentsFilterComponent } from '../../../Monitor/Shared/components/AgentsFilter/agents-filter.component';
import { loadAgentFilterOptions } from '../../../Monitor/Shared/components/AgentsFilter/agents-filter-options';

@Injectable()
export class MentionsNoFacetFilterService extends FilterService implements OnDestroy {

    private subscriptions: Subscription[] = [];
    private searchFilters: MentionSearchCriteriaModel;
    private searchFilters$: BehaviorSubject<MentionSearchCriteriaModel> = new BehaviorSubject<MentionSearchCriteriaModel>(null);
    protected userTimezonePreference: string;

    // content filters
    public readonly keywords = new MonitoringKeywordFilterModel(
        monitoringFiltersEnum.keywords,
        'Monitoring.filtersLocalization.keywords',
        MonitoringKeywordFilterComponent,
        this.localizationService,
        FilterSectionEnum.CONTENT
    );

    private readonly mediaTypes = new MediaTypeTreeFilterModel<MediaTypeModel>(
        monitoringFiltersEnum.mediaTypes,
        'Monitoring.filtersLocalization.mediaTypes',
        MediaTypeTreeFilterComponent,
        this.localizationService,
        MediaTypeModel.getAllMediaTypes().map((mediaType) => {
            const res = {
                label: mediaType.name,
                value: mediaType
            } as FilterOptionModel<MediaTypeModel>;

            if (mediaTypesWithSubTypesMap.has(mediaType.id)) {
                mediaType.children = mediaTypesWithSubTypesMap.get(mediaType.id).map(subType => {
                    return {
                        label: subType.name,
                        value: subType
                    } as FilterOptionModel<MediaTypeModel>;
                });
            }

            return res;
        }),
        new SelectedMediaTypesFilterModel([], false),
        FilterSectionEnum.CONTENT,
        false,
        true
    );

    private readonly authors = new FilterTypeTextModel(
        monitoringFiltersEnum.authors,
        'Monitoring.filtersLocalization.authors',
        undefined,
        undefined,
        true,
        false,
        undefined,
        FilterSectionEnum.CONTENT,
        undefined,
        true
    );

    private readonly outlets = new FilterTypeTextModel(
        monitoringFiltersEnum.outlets,
        'Monitoring.filtersLocalization.outlets',
        undefined,
        undefined,
        true,
        false,
        undefined,
        FilterSectionEnum.CONTENT,
        undefined,
        true
    );

    private readonly languages = new FilterTypeCheckboxModel<LanguageModel>(
        monitoringFiltersEnum.languages,
        'Monitoring.filtersLocalization.languages',
        Object.keys(isoLanguageNames)
            .map((isoCode) => {
                const model = new LanguageModel(isoCode);

                return {
                    label: isoLanguageNames[isoCode],
                    value: model
                };
            })
            .sort((a, b) => a.label.localeCompare(b.label))
        ,
        true,
        undefined,
        undefined,
        undefined,
        undefined,
        FilterSectionEnum.CONTENT,
        true
    );

    public readonly hashtags = new FilterTypeTextModel(
        monitoringFiltersEnum.hashtags,
        'Monitoring.filtersLocalization.hashtags',
        undefined,
        undefined,
        true,
        false,
        undefined,
        FilterSectionEnum.CONTENT,
        undefined,
        true
    );

    // Location Section
    private readonly regions = new FilterTypeAutocompleteModel<JournodexGeographyModel>(
        monitoringFiltersEnum.regions,
        'Monitoring.filtersLocalization.regions',
        (query) => this.searchRegions(query),
        undefined,
        FilterSectionEnum.LOCATION,
        true
    );

    // Analysis Section
    private readonly audience = new FilterTypeRangeModel(
        monitoringFiltersEnum.audience,
        'Monitoring.filtersLocalization.audience',
        0,
        Mention.maxAudienceReach,
        undefined,
        false,
        undefined,
        undefined,
        undefined,
        FilterSectionEnum.ANALYSIS,
        undefined,
        true
    );

    private readonly sentiment = new FilterTypeCheckboxModel<SentimentModel>(
        monitoringFiltersEnum.sentiment,
        'Monitoring.filtersLocalization.sentiment',
        [
            {
                label: `Sentiment.${SearchCriteriaQuerySentimentsEnum.positive}`,
                value: new SentimentModel(SearchCriteriaQuerySentimentsEnum.positive)
            },
            {
                label: `Sentiment.${SearchCriteriaQuerySentimentsEnum.negative}`,
                value: new SentimentModel(SearchCriteriaQuerySentimentsEnum.negative)
            },
            {
                label: `Sentiment.${SearchCriteriaQuerySentimentsEnum.neutral}`,
                value: new SentimentModel(SearchCriteriaQuerySentimentsEnum.neutral)
            },
            {
                label: `Sentiment.${SearchCriteriaQuerySentimentsEnum.notAvailable}`,
                value: new SentimentModel(SearchCriteriaQuerySentimentsEnum.notAvailable)
            },
        ],
        false,
        undefined,
        undefined,
        undefined,
        undefined,
        FilterSectionEnum.ANALYSIS
    );

    private readonly ave = new FilterTypeRangeModel(
        monitoringFiltersEnum.ave,
        'Monitoring.filtersLocalization.ave',
        0,
        Mention.maxAVE,
        undefined,
        true,
        undefined,
        undefined,
        undefined,
        FilterSectionEnum.ANALYSIS,
    );

    public readonly prominence = new ProminenceFilterModel(
        monitoringFiltersEnum.prominence,
        'Monitoring.filtersLocalization.prominence',
        ProminenceFilterComponent,
        this.localizationService,
        FilterSectionEnum.ANALYSIS,
        true
    );

    public readonly domainAuthority = new FilterTypeRangeModel(
        monitoringFiltersEnum.domainAuthority,
        'Monitoring.filtersLocalization.domainAuthority',
        1,
        Mention.maxDomainAuthorityScore,
        undefined,
        undefined,
        undefined,
        true,
        undefined,
        FilterSectionEnum.ANALYSIS
    );

    public readonly engagement = new EngagementFilterModel(
        monitoringFiltersEnum.engagementTotal,
        'Monitoring.filtersLocalization.engagements',
        EngagementFilterComponent,
        FilterSectionEnum.ANALYSIS
    );

    public readonly engagementRate = new FilterTypeRangeModel(
        monitoringFiltersEnum.engagementRate,
        'Monitoring.filtersLocalization.engagementRate',
        0,
        Mention.maxEngagementRate - 1, // If we set it to max it will end up as undefined when set
        '%',
        undefined,
        undefined,
        undefined,
        true,
        FilterSectionEnum.ANALYSIS,
        true
    );

    // Organization Section
    // No facets here, so agents are listed without counts
    public readonly aiAgents = new AgentsFilterModel(
        monitoringFiltersEnum.aiAgent,
        'Monitoring.filtersLocalization.aiAgents',
        AgentsFilterComponent,
        FilterSectionEnum.ORGANIZATION,
        true
    );

    private readonly tags = new FilterTypeCheckboxModel<number>(
        monitoringFiltersEnum.tags,
        'Monitoring.filtersLocalization.tags',
        [],
        true,
        undefined,
        undefined,
        undefined,
        undefined,
        FilterSectionEnum.ORGANIZATION,
        true
    );

    public readonly tagGroups = new FilterTypeCheckboxModel<number>(
        monitoringFiltersEnum.tagGroups,
        'Monitoring.filtersLocalization.tagGroups',
        [],
        true,
        undefined,
        undefined,
        undefined,
        undefined,
        FilterSectionEnum.ORGANIZATION,
        true
    );

    public readonly sourceGroups = new FilterTypeCheckboxModel<number>(
        monitoringFiltersEnum.sourceGroups,
        'Monitoring.filtersLocalization.sourceGroups',
        [],
        true,
        undefined,
        undefined,
        undefined,
        undefined,
        FilterSectionEnum.ORGANIZATION,
        true
    );

    public readonly discoverLists = new DiscoverListsFilterModel(
        monitoringFiltersEnum.discoverLists,
        'Monitoring.filtersLocalization.discoverLists',
        DiscoverListsFilterComponent,
        FilterSectionEnum.ORGANIZATION,
        true
    );

    public readonly duplicates = new FilterTypeDuplicatesModel(
        monitoringFiltersEnum.duplicates,
        'Monitoring.filtersLocalization.duplicates',
        false,
        undefined,
        FilterSectionEnum.ORGANIZATION
    );

    private requests: Subscription[] = [];

    private userHasAgents: boolean = false;
    private aiAgentsRequest: Subscription;
    /** The filters last asked for, before any the account can't use were taken out */
    private requestedFilters: FilterTypeModel[] = [];

    // order matters - https://agility-confluence.atlassian.net/wiki/spaces/AM/pages/666680/Mention+List+Filters
    protected allFilters = [
        // Content Section
        this.keywords,
        this.mediaTypes,
        this.authors,
        this.outlets,
        this.languages,
        this.hashtags,

        // Location Section
        this.regions,

        // Analysis Section
        this.audience,
        this.sentiment,
        this.ave,
        this.prominence,

        this.domainAuthority,
        this.engagement,
        this.engagementRate,

        // Organization Section
        this.aiAgents,
        this.tags,
        this.tagGroups,
        this.sourceGroups,
        this.discoverLists,
        this.duplicates,
    ];

    // for briefing curated rule
    restrictedFilters = [
        // Content Section
        this.mediaTypes,
        this.authors,
        this.outlets,
        this.languages,

        // Location Section
        this.regions,

        // Analysis Section
        this.audience,
        this.sentiment,
        this.ave,

        // Organization Section
        this.aiAgents,
        this.tags,
    ];

    protected orderedAccountFilters = [
        // Content Section
        this.keywords,
        this.mediaTypes,
        this.authors,
        this.outlets,
        this.languages,
        this.hashtags,

        // Location Section
        this.regions,

        // Analysis Section
        this.audience,
        this.sentiment,
        this.ave,
        this.prominence,
        // Briefing does not support domainAuthority yet
        this.domainAuthority,
        this.engagement,
        this.engagementRate,

        // Organization Section
        this.aiAgents,
        this.tags,
        this.tagGroups,
        this.sourceGroups,
        this.discoverLists,
        this.duplicates,
    ];

    protected orderedMercuryFilters = [
        // Content Section
        this.keywords,
        this.mediaTypes,
        this.authors,
        this.outlets,
        this.languages,
        this.hashtags,

        // Location Section
        this.regions,

        // Analysis Section
        this.audience,
        this.sentiment,
        this.ave,
        this.domainAuthority,
        this.engagement,
        this.engagementRate,

        // Organization Section
        this.sourceGroups,
        this.discoverLists,
        this.duplicates,
    ];

    constructor(
        private tagsService: TagsService,
        private journodexService: JournodexRegionsService,
        private tagGroupsService: TagGroupsService,
        private sourceGroupService: SourceGroupService,
        private localizationService: LocalizationService,
        private accountPreferencesService: AccountPreferencesService,
        private userStateService: CurrentUserStateService,
        private savedFilterService: SavedFilterService,
        private currencyConversionService: CurrencyConversionService,
        private discoverListsFilterService: DiscoverListsFilterService,
        private injector: Injector
    ) {
        super();

        this.subscriptions.push(
            // Ahead of the first setFilters so it can leave the agents in, then again on any change
            this.userStateService.getAgenticFeatureEnabled().subscribe(enabled => {
                this.userHasAgents = enabled;
                this.setFilters(this.requestedFilters);
            })
        );

        this.setFilters(this.restrictedFilters);

        this.subscriptions.push(
            this.tagsService.all$.subscribe(items => {
                if (!items) {
                    this.tags.options = [];
                    return;
                }
                this.tags.options = items.map((item) => {
                    return {
                        label: item.name,
                        value: item.id
                    };
                });
                this.sourceGroups.options.sort((a, b) => a.label.localeCompare(b.label));
            }),
            this.tagGroupsService.getTagGroupsList().subscribe(items => {
                if (!items) {
                    this.tagGroups.options = [];
                    return;
                }
                this.tagGroups.options = items.map((item) => {
                    return {
                        label: item.name,
                        value: item.id
                    };
                });
                this.tagGroups.options.sort((a, b) => a.label.localeCompare(b.label));
            }),
            this.sourceGroupService.all$.subscribe(items => {
                if (!items) {
                    this.sourceGroups.options = [];
                    return;
                }
                this.sourceGroups.options = items.map((item) => {
                    return {
                        label: item.name,
                        value: item.id
                    };
                });
                this.sourceGroups.options.sort((a, b) => a.label.localeCompare(b.label));
            }),
            this.accountPreferencesService.prominenceGroups$.subscribe(items => {
                if (!items) {
                    return;
                }

                this.getProminenceOptions(this.prominence, items.map(item => {
                    return {
                        prominenceGroup: item,
                        ranges: [{count: null}]
                    };
                }));
            }),
            this.userStateService.getPreferences().subscribe(
                preferences => {
                    if (preferences) {
                        this.userTimezonePreference = preferences.timeZone;
                    }
                }
            ),
            this.savedFilterService.all$.subscribe(savedFilters => this.updateSavedFilters(savedFilters))
        );
    }




    /** Agents are only offered to accounts that have them, and only loaded once they are offered */
    protected setFilters(filters: FilterTypeModel[]): void {
        this.requestedFilters = filters;

        if (!this.userHasAgents) {
            filters = filters.filter(filterType => filterType !== this.aiAgents);
        } else if (filters.includes(this.aiAgents) && !this.aiAgentsRequest) {
            this.aiAgents.loading = true;
            this.aiAgentsRequest = loadAgentFilterOptions(this.injector).subscribe(options => {
                this.aiAgents.setOptions(options);
                this.aiAgents.loading = false;
                this.setFilters(this.requestedFilters);
            });
            this.subscriptions.push(this.aiAgentsRequest);
        }

        super.setFilters(filters);
    }

    getSearchFilters(): Observable<MentionSearchCriteriaModel> {
        return this.searchFilters$;
    }

    setSearchFilters(filters: MentionSearchCriteriaModel): void {
        this.searchFilters = filters;
        this.searchFilters$.next(this.searchFilters);
    }

    private searchRegions(query: string): Observable<FilterOptionModel<JournodexGeographyModel>[]> {
        return this.journodexService.searchGeographicLocations(query).pipe(
            map(regions => {
                return regions.map((region) => {
                    return {
                        label: region.displayString,
                        value: region
                    };
                });
            }));
    }

    /**
     * Resets the filters to their empty states
     */
    public clearFilters(): void {
        super.clearFilters();

        this.requests.forEach(sub => sub.unsubscribe());
        this.requests.length = 0;
    }

    /**
     * Creates a search query from the current filters
     * dateQuery is just used when we want to force / pass a date query after a reset / begining of some logic
     * because new MentionSearchCriteriaModel sets it back to 24hrs (monitoring source picker, resets it when you switch sources from blank)
     */

    generateFilterQuery(dateQuery?: DateQueryModel): MentionSearchQueryModel {
        if (!this.searchFilters) {
            this.searchFilters = new MentionSearchCriteriaModel();
            if (dateQuery) {
                this.searchFilters.query.dateQuery = dateQuery;
            }
        } else {
            this.searchFilters = this.searchFilters.clone();
        }
        const query = this.searchFilters.query;

        if (query.dateQuery && !query.dateQuery.timezone) {
            query.dateQuery.timezone = this.userTimezonePreference;
        }

        if (this.keywords.getFilterMode() === KeywordFilterModeEnum.standard) {
            query.keywordGroups = this.keywords.getKeywordGroupsForSearchQuery();
            query.searchMode = SearchMode.standard;
        } else {
            if (this.keywords.getCurrentValue().expertQuery?.filter !== '') {
                const keywords = this.keywords.getCurrentValue().expertQuery;
                query.expertBooleanQuery.filter = keywords.filter;
                query.expertBooleanQuery.searchContexts = [...keywords.searchContexts];
                query.searchMode = SearchMode.expert;
            }
        }

        query.includedSources = [];
        query.excludedSources = [];

        if (this.authors.isInclude()) {
            query.includedSources.push(...this.authors.getCurrentValue().map(name => {
                const model = new JournodexSourceModel();
                model.entityType = SearchCriteriaSourcesEnum.journalist;
                model.entityName = name;

                return model;
            }));

        } else {
            query.excludedSources.push(...this.authors.getCurrentValue().map(name => {
                const model = new JournodexSourceModel();
                model.entityType = SearchCriteriaSourcesEnum.journalist;
                model.entityName = name;

                return model;
            }));
        }

        if (this.outlets.isInclude()) {
            query.includedSources.push(...this.outlets.getCurrentValue().map(name => {
                const model = new JournodexSourceModel();
                model.entityType = SearchCriteriaSourcesEnum.outlet;
                model.entityName = name;

                return model;
            }));
        } else {
            query.excludedSources.push(...this.outlets.getCurrentValue().map(name => {
                const model = new JournodexSourceModel();
                model.entityType = SearchCriteriaSourcesEnum.outlet;
                model.entityName = name;

                return model;
            }));
        }

        query.sentiments = this.sentiment.getCurrentValue();

        const selectedMediaTypeFilter = this.mediaTypes.getCurrentValue();
        const twitterChildren = this.mediaTypes.getCurrentValue().mediaTypes.find(media => {
            return (media.id === MediaTypeEnum.TWITTER);
        })?.children || [];
        const linkedinChildren = this.mediaTypes.getCurrentValue().mediaTypes.find(media => {
            return (media.id === MediaTypeEnum.LINKEDIN);
        })?.children || [];
        const blueskyChildren = this.mediaTypes.getCurrentValue().mediaTypes.find(media => {
            return (media.id === MediaTypeEnum.BLUESKY);
        })?.children || [];
        const tvChildren = this.mediaTypes.getCurrentValue().mediaTypes.find(media => {
            return (media.id === MediaTypeEnum.TV);
        })?.children || [];

        if (this.mediaTypes.isInclude()) {
            query.includedMediaTypes = selectedMediaTypeFilter.mediaTypes;
            query.excludedMediaTypes = [];

            query.includedTweetTypes = twitterChildren.map(media => media.value);
            query.excludedTweetTypes = [];

            query.includedLinkedinTypes = linkedinChildren.map(media => media.value);
            query.excludedLinkedinTypes = [];

            query.includedBlueskyTypes = blueskyChildren.map(media => media.value);
            query.excludedBlueskyTypes = [];

            query.includedTvTypes = tvChildren.map(media => media.value);
            query.excludedTvTypes = [];
        } else {
            query.includedMediaTypes = [];
            query.includedTweetTypes = [];
            query.includedLinkedinTypes = [];
            query.includedBlueskyTypes = [];
            query.includedTvTypes = [];

            let excludeAllX = true;
            let excludeAllLinkedIn = true;
            let excludeAllBluesky = true;
            let excludeAllTv = true;
            Object.values(TwitterSubTypeEnum).forEach(type => {
                if (!twitterChildren.some(x => x.value.childId === type)) {
                    excludeAllX = false;
                }
            });
            Object.values(LinkedinSubTypeEnum).forEach(type => {
                if (!linkedinChildren.some(x => x.value.childId === type)) {
                    excludeAllLinkedIn = false;
                }
            });
            Object.values(BlueskySubTypeEnum).forEach(type => {
                if (!blueskyChildren.some(x => x.value.childId === type)) {
                    excludeAllBluesky = false;
                }
            });
            Object.values(BroadcastSubTypeEnum).forEach(type => {
                if (!tvChildren.some(x => x.value.childId === type)) {
                    excludeAllTv = false;
                }
            });

            query.excludedMediaTypes = selectedMediaTypeFilter.mediaTypes;

            if (excludeAllX || !twitterChildren.length) {
                query.excludedTweetTypes = [];
            } else {
                query.excludedTweetTypes = twitterChildren.map(media => media.value);
                query.excludedMediaTypes = query.excludedMediaTypes.filter(media => media.id !== MediaTypeEnum.TWITTER);
            }

            if (excludeAllLinkedIn || !linkedinChildren.length) {
                query.excludedLinkedinTypes = [];
            } else {
                query.excludedLinkedinTypes = linkedinChildren.map(media => media.value);
                query.excludedMediaTypes = query.excludedMediaTypes.filter(media => media.id !== MediaTypeEnum.LINKEDIN);
            }

            if (excludeAllBluesky || !blueskyChildren.length) {
                query.excludedBlueskyTypes = [];
            } else {
                query.excludedBlueskyTypes = blueskyChildren.map(media => media.value);
                query.excludedMediaTypes = query.excludedMediaTypes.filter(media => media.id !== MediaTypeEnum.BLUESKY);
            }

            if (excludeAllTv || !tvChildren.length) {
                query.excludedTvTypes = [];
            } else {
                query.excludedTvTypes = tvChildren.map(media => media.value);
                query.excludedMediaTypes = query.excludedMediaTypes.filter(media => media.id !== MediaTypeEnum.TV);
            }
        }

        if (selectedMediaTypeFilter.archivedOnly) {
            query.isArchivedMention = selectedMediaTypeFilter.archivedOnly;
        }

        query.audienceReach = this.audience.getCurrentValue();
        const range = this.ave.getCurrentValue();
        query.ave = {min: this.currencyConversionService.convertUserCurrencyToUSD(range.min),
            max: this.currencyConversionService.convertUserCurrencyToUSD(range.max), currency: 'USD'};
        if (this.languages.isInclude()) {
            query.includedLanguages = this.languages.getCurrentValue();
            query.excludedLanguages = [];
        } else {
            query.excludedLanguages = this.languages.getCurrentValue();
            query.includedLanguages = [];
        }

        if (this.regions.isInclude()) {
            query.includedRegions = this.regions.getCurrentValue().map(option => option.value);
            query.excludedRegions = [];
        } else {
            query.excludedRegions = this.regions.getCurrentValue().map(option => option.value);
            query.includedRegions = [];
        }
        query.rawIncludedRegions = null;
        query.rawExcludedRegions = null;

        if (this.hashtags.isInclude()) {
            query.hashtags.include = this.hashtags.getCurrentValue();
            query.hashtags.exclude = [];
        } else {
            query.hashtags.exclude = this.hashtags.getCurrentValue();
            query.hashtags.include = [];
        }

        if (this.filters.includes(this.tags)) {
            if (this.tags.isInclude()) {
                query.tag.include = this.tags.getCurrentValue();
                query.tag.exclude = [];
            } else {
                query.tag.exclude = this.tags.getCurrentValue();
                query.tag.include = [];
            }
        }

        if (this.filters.includes(this.tagGroups)) {
            if (this.tagGroups.isInclude()) {
                query.tagGroup.include = this.tagGroups.getCurrentValue();
                query.tagGroup.exclude = [];
            } else {
                query.tagGroup.exclude = this.tagGroups.getCurrentValue();
                query.tagGroup.include = [];
            }
        }

        if (this.filters.includes(this.duplicates)) {
            this.searchFilters.showSimilar = this.duplicates.getCurrentValue() !== DuplicatesStatusEnum.hide;
        }
        if (this.savedFilters?.getValue().length) {
            this.searchFilters.savedFilterIds = this.savedFilters.getValue().map(x => x.id);
        }

        if (this.filters.includes(this.sourceGroups)) {
            if (this.sourceGroups.isInclude()) {
                query.sourceGroup.include = this.sourceGroups.getCurrentValue();
                query.sourceGroup.exclude = [];
            } else {
                query.sourceGroup.exclude = this.sourceGroups.getCurrentValue();
                query.sourceGroup.include = [];
            }
        }

        if (this.filters.includes(this.prominence)) {
            if (this.prominence.isInclude()) {
                query.includedProminenceFilter = this.prominence.getCurrentValue()?.filters;
                query.excludedProminenceFilter = [];
            } else {
                query.excludedProminenceFilter = this.prominence.getCurrentValue()?.filters;
                query.includedProminenceFilter = [];
            }
        }

        if (this.filters.includes(this.domainAuthority)) {
            query.domainAuthority = this.domainAuthority.getCurrentValue();
        }

        if (this.filters.includes(this.engagement)) {
            query.engagementFilter = {
                ...query.engagementFilter,
                ...this.engagement.getCurrentValue(),
            };
        }

        if (this.filters.includes(this.engagementRate)) {
            query.engagementFilter.engagementRate = this.engagementRate.getCurrentValue();
        }

        if (this.filters.includes(this.aiAgents)) {
            Object.assign(query, this.aiAgents.toQuery());
        }

        if (this.filters.includes(this.discoverLists)) {
            query.discoverListFilter = this.discoverLists.hasCurrentValue() ? this.discoverLists.getCurrentValue() : null;
        }

        return query;

    }

    // HACK: dateQuery is just used to pass to generateFilterQuery
    public applyFiltersToSearch(dateQuery?: DateQueryModel): void {
        const query = this.generateFilterQuery(dateQuery);
        this.searchFilters.query = query;
        this.searchFilters.showSimilar = this.duplicates.getValue() !== DuplicatesStatusEnum.hide;

        if (this.savedFilters.getValue().length > 0) {
            this.checkForSavedFilterConflicts();
            this.searchFilters.savedFilterIds = this.savedFilters.getValue().map(x => x.id);
        } else {
            this.searchFilters.savedFilterIds = [];
        }

        this.setSearchFilters(this.searchFilters);
    }

    /**
     * Gets the filters from a search object and applies them to the current filters
     */
    public getFiltersFromSearch(search: MentionSearchCriteriaModel): void {
        const query = search.query;
        this.applySearchQueryToFilters(query);
    }

    applyFilters(): void {
        super.applyFilters();
        this.applyFiltersToSearch();
    }

    /**
     * Apply filters from a search object to the current filters
     */
    applySearchQueryToFilters(query: MentionSearchQueryModel) {
        const authorsIncluded = query.includedSources.filter(source => source.entityType === SearchCriteriaSourcesEnum.journalist);
        const authorsExcluded = query.excludedSources.filter(source => source.entityType === SearchCriteriaSourcesEnum.journalist);

        if (authorsIncluded.length) {
            this.authors.setValue(authorsIncluded.map(author => author.entityName));
            this.authors.setIncludeExclude(IncludeExcludeEnum.include);
        } else if (authorsExcluded.length){
            this.authors.setValue(authorsExcluded.map(author => author.entityName));
            this.authors.setIncludeExclude(IncludeExcludeEnum.exclude);
        }

        const outletsIncluded = query.includedSources.filter(source => source.entityType === SearchCriteriaSourcesEnum.outlet);
        const outletsExcluded = query.excludedSources.filter(source => source.entityType === SearchCriteriaSourcesEnum.outlet);
        if (outletsIncluded.length) {
            this.outlets.setValue(outletsIncluded.map(outlet => outlet.entityName));
            this.outlets.setIncludeExclude(IncludeExcludeEnum.include);
        } else if (outletsExcluded.length){
            this.outlets.setValue(outletsExcluded.map(outlet => outlet.entityName));
            this.outlets.setIncludeExclude(IncludeExcludeEnum.exclude);
        }
        this.sentiment.setValue(query.sentiments);
        this.sentiment.setValue(
            query.sentiments.map(
                (sentiment) => {
                    const opt = this.sentiment.options.find(option => option.value.id === sentiment.id);

                    return opt.value;
                }
            )
        );

        if (query.searchMode === SearchMode.expert) {
            this.keywords.setFilterMode(KeywordFilterModeEnum.expert);
            this.keywords.setValue(
                {
                    keywordGroups: [],
                    expertQuery: query.expertBooleanQuery.filter !== '' ? query.expertBooleanQuery : new MonitoringAdvancedBooleanModel()
                }
            );

        } else {
            this.keywords.setValue(
                {
                    keywordGroups: [ ...query.keywordGroups ],
                    expertQuery: new MonitoringAdvancedBooleanModel()
                }
            );
        }

        // populate twitter parent with children
        if (query.includedTweetTypes.length > 0) {
            const twitterParent = query.includedMediaTypes.find(opt => opt.id === MediaTypeEnum.TWITTER);
            if (twitterParent)  {
                twitterParent.children = query.includedTweetTypes.map(type => {
                    return <FilterOptionModel>{
                        label: type.name,
                        value: type
                    };
                });
            }
        } else if (query.excludedTweetTypes.length) {
            let twitterParent = query.excludedMediaTypes.find(opt => opt.id === MediaTypeEnum.TWITTER);
            if (!twitterParent) {
                twitterParent = new MediaTypeModel(MediaTypeEnum.TWITTER);
                query.excludedMediaTypes.push(twitterParent);
            }

            twitterParent.children = query.excludedTweetTypes.map(type => {
                return <FilterOptionModel>{
                    label: type.name,
                    value: type
                };
            });
        }

        // populate linkedin parent with children
        if (query.includedLinkedinTypes.length > 0) {
            const linkedinParent = query.includedMediaTypes.find(opt => opt.id === MediaTypeEnum.LINKEDIN);
            if (linkedinParent)  {
                linkedinParent.children = query.includedLinkedinTypes.map(type => {
                    return <FilterOptionModel>{
                        label: type.name,
                        value: type
                    };
                });
            }
        } else if (query.excludedLinkedinTypes.length) {
            let linkedinParent = query.excludedMediaTypes.find(opt => opt.id === MediaTypeEnum.LINKEDIN);
            if (!linkedinParent) {
                linkedinParent = new MediaTypeModel(MediaTypeEnum.LINKEDIN);
                query.excludedMediaTypes.push(linkedinParent);
            }

            linkedinParent.children = query.excludedLinkedinTypes.map(type => {
                return <FilterOptionModel>{
                    label: type.name,
                    value: type
                };
            });
        }

        // populate bluesky parent with children
        if (query.includedBlueskyTypes.length > 0) {
            const blueskyParent = query.includedMediaTypes.find(opt => opt.id === MediaTypeEnum.BLUESKY);
            if (blueskyParent)  {
                blueskyParent.children = query.includedBlueskyTypes.map(type => {
                    return <FilterOptionModel>{
                        label: type.name,
                        value: type
                    };
                });
            }
        } else if (query.excludedBlueskyTypes.length) {
            let blueskyParent = query.excludedMediaTypes.find(opt => opt.id === MediaTypeEnum.BLUESKY);
            if (!blueskyParent) {
                blueskyParent = new MediaTypeModel(MediaTypeEnum.BLUESKY);
                query.excludedMediaTypes.push(blueskyParent);
            }

            blueskyParent.children = query.excludedBlueskyTypes.map(type => {
                return <FilterOptionModel>{
                    label: type.name,
                    value: type
                };
            });
        }

        // populate tv parent with children
        if (query.includedTvTypes.length > 0) {
            const tvParent = query.includedMediaTypes.find(opt => opt.id === MediaTypeEnum.TV);
            if (tvParent)  {
                tvParent.children = query.includedTvTypes.map(type => {
                    return <FilterOptionModel>{
                        label: type.name,
                        value: type
                    };
                });
            }
        } else if (query.excludedTvTypes.length) {
            let tvParent = query.excludedMediaTypes.find(opt => opt.id === MediaTypeEnum.TV);
            if (!tvParent) {
                tvParent = new MediaTypeModel(MediaTypeEnum.TV);
                query.excludedMediaTypes.push(tvParent);
            }

            tvParent.children = query.excludedTvTypes.map(type => {
                return <FilterOptionModel>{
                    label: type.name,
                    value: type
                };
            });
        }


        if (query.includedMediaTypes?.length) {
            this.mediaTypes.setValue(new SelectedMediaTypesFilterModel(query.includedMediaTypes, query.isArchivedMention));
            this.mediaTypes.setIncludeExclude(IncludeExcludeEnum.include);
        } else if (query.excludedMediaTypes?.length) {
            this.mediaTypes.setValue(new SelectedMediaTypesFilterModel(query.excludedMediaTypes, query.isArchivedMention));
            this.mediaTypes.setIncludeExclude(IncludeExcludeEnum.exclude);
        }

        this.audience.setValue(query.audienceReach);
        this.ave.setValue({
            min: +this.currencyConversionService.convertCurrency(query.ave.min, false),
            max: +this.currencyConversionService.convertCurrency(query.ave.max, false)
        });

        const prominenceFilter = new ProminenceFilterApiModel();

        if (query.includedProminenceFilter?.length) {
            prominenceFilter.filters = query.includedProminenceFilter;
            this.prominence.setValue(prominenceFilter);
            this.prominence.setIncludeExclude(IncludeExcludeEnum.include);
        } else if (query.excludedProminenceFilter?.length) {
            prominenceFilter.filters = query.excludedProminenceFilter;
            this.prominence.setValue(prominenceFilter);
            this.prominence.setIncludeExclude(IncludeExcludeEnum.exclude);
        }

        if (query.hashtags?.include?.length) {
            this.hashtags.setValue(query.hashtags.include);
            this.hashtags.setIncludeExclude(IncludeExcludeEnum.include);
        } else if (query.hashtags?.exclude?.length) {
            this.hashtags.setValue(query.hashtags.exclude);
            this.hashtags.setIncludeExclude(IncludeExcludeEnum.exclude);
        }

        const languageValues = this.languages.options.map(langOpt => langOpt.value);
        const languagesIncluded = query.includedLanguages.map(lang => {
            return languageValues.find(langVal => langVal.id === lang.id);
        });
        const languagesExcluded = query.excludedLanguages.map(lang => {
            return languageValues.find(langVal => langVal.id === lang.id);
        });
        if (languagesIncluded.length) {
            this.languages.setValue(languagesIncluded);
            this.languages.setIncludeExclude(IncludeExcludeEnum.include);
        } else if (languagesExcluded.length){
            this.languages.setValue(languagesExcluded);
            this.languages.setIncludeExclude(IncludeExcludeEnum.exclude);
        }

        if (query.rawIncludedRegions) {
            this.journodexService.mapToComplexRegionInfo(query.rawIncludedRegions).subscribe(
                (regions) => {
                    this.regions.setValue(regions.map(region => {
                        return {
                            label: region.displayString,
                            value: region
                        };
                    }));
                    this.setFilters(this.filters);
                }
            );
            this.regions.setIncludeExclude(IncludeExcludeEnum.include);
        } else if (query.includedRegions?.length) {
            this.regions.setValue(query.includedRegions.map(region => {
                return {
                    label: region.displayString,
                    value: region
                };
            }));
            this.regions.setIncludeExclude(IncludeExcludeEnum.include);
        } else if (query.rawExcludedRegions) {
            this.journodexService.mapToComplexRegionInfo(query.rawExcludedRegions).subscribe(
                (regions) => {
                    this.regions.setValue(regions.map(region => {
                        return {
                            label: region.displayString,
                            value: region
                        };
                    }));
                    this.setFilters(this.filters);
                }
            );
            this.regions.setIncludeExclude(IncludeExcludeEnum.exclude);
        } else if (query.excludedRegions?.length) {
            this.regions.setValue(query.excludedRegions.map(region => {
                return {
                    label: region.displayString,
                    value: region
                };
            }));
            this.regions.setIncludeExclude(IncludeExcludeEnum.exclude);
        }

        if (query.tag.include.length > 0) {
            this.tags.setValue(query.tag.include);
            this.tags.setIncludeExclude(IncludeExcludeEnum.include);
        } else if (query.tag.exclude.length) {
            this.tags.setValue(query.tag.exclude);
            this.tags.setIncludeExclude(IncludeExcludeEnum.exclude);
        }

        if (query.tagGroup.include.length > 0) {
            this.tagGroups.setValue(query.tagGroup.include);
            this.tagGroups.setIncludeExclude(IncludeExcludeEnum.include);
        } else if (query.tagGroup.exclude.length) {
            this.tagGroups.setValue(query.tagGroup.exclude);
            this.tagGroups.setIncludeExclude(IncludeExcludeEnum.exclude);
        }

        if (AgentsFilterModel.hasQuery(query)) {
            this.aiAgents.setFromQuery(query);
        }

        if (query.sourceGroup.include.length) {
            this.sourceGroups.setValue(query.sourceGroup.include);
            this.sourceGroups.setIncludeExclude(IncludeExcludeEnum.include);
        } else if (query.sourceGroup.exclude.length) {
            this.sourceGroups.setValue(query.sourceGroup.exclude);
            this.sourceGroups.setIncludeExclude(IncludeExcludeEnum.exclude);
        }

        this.engagement.setValue({
            engagementTotal: query.engagementFilter.engagementTotal,
            amplification: query.engagementFilter.amplification,
            conversation: query.engagementFilter.conversation,
            applause: query.engagementFilter.applause,
            view: query.engagementFilter.view,
        });
        this.engagementRate.setValue(query.engagementFilter.engagementRate);
        this.domainAuthority.setValue(query.domainAuthority);

        if(query.discoverListFilter){
            this.discoverListsFilterService.populateFilter(this.discoverLists, query.discoverListFilter).subscribe(
                () => this.setFilters(this.filters)
            );
        }

        this.setFilters(this.filters);
    }

    /**
     * Sets the duplicate filter.
     */
    setDuplicateFilter(value: DuplicatesStatusEnum): void {
        this.duplicates.setValue(value);
    }

    setSavedFiltersFromIds(value: number[]): void {
        if (value) {
            const savedFilters: SavedFilter[] = [];
            value.forEach(id => {
                const matchFilter = this.savedFilters.options.find(x => x.value.id === id)?.value;
                if (matchFilter) {
                    savedFilters.push(matchFilter);
                }
            });
            this.savedFilters.setValue(savedFilters);
            this.savedFilters.filterCards = savedFilters.map(
                sf => this.constructFilterCardFromValue(sf)
            );
        }
    }


    public setOrderedAccountFilters(filters: monitoringFiltersEnum[]): void {
        this.orderedAccountFilters = this.orderedAccountFilters.filter(x => filters.includes(x.id as monitoringFiltersEnum));
    }

    public setOrderedMercuryFilters(filters: monitoringFiltersEnum[]): void {
        this.orderedMercuryFilters = this.orderedMercuryFilters.filter(x => filters.includes(x.id as monitoringFiltersEnum));
    }

    /**
     * Sets the filters available for folder, tag, tagGroup, sourceGroup results
     */
    public setAccountFilters(sourceType: MonitoringSeriesTypeEnum): void {
        switch (sourceType) {
            case MonitoringSeriesTypeEnum.allSavedCoverage:
            case MonitoringSeriesTypeEnum.folder:
            case MonitoringSeriesTypeEnum.tag:
            case MonitoringSeriesTypeEnum.tagGroup:
                this.setFilters([this.savedFilters, ...this.orderedAccountFilters]);
                break;
            default:
                this.setFilters([this.savedFilters, ...this.orderedMercuryFilters]);
        }
    }

    setRestrictedFilters(): void {
        this.setFilters(this.restrictedFilters);
    }

    setAllFilters(): void {
        this.setFilters(this.allFilters);
    }

    public setDateQuery(dateQuery: DateQueryModel): void {
        if (!this.searchFilters) {
            return;
        }

        dateQuery.timezone = this.userTimezonePreference;
        this.searchFilters.query.dateQuery = dateQuery;
    }

    ungroupSavedFilter(savedFilter: SavedFilter): void {
        const filterQuery = savedFilter.query;
        // if either source filter or selected saved filter is using expert search mode, all keywords must be converted to expert
        if (
            this.keywords.getFilterMode() === KeywordFilterModeEnum.expert
            || filterQuery.expertBooleanQuery?.filter.length > 0
        ) {
            const keywordFilter = this.keywords.getCurrentValue();
            const expertBooleanQuery: MonitoringAdvancedBooleanModel = new MonitoringAdvancedBooleanModel();
            expertBooleanQuery.searchContexts = this.keywords.getFilterMode() === KeywordFilterModeEnum.expert ?
                keywordFilter.expertQuery.searchContexts
                : keywordFilter.keywordGroups[0].searchContexts;

            // Saved filter expert && source filter expert
            if (filterQuery.expertBooleanQuery?.filter.length > 0) {

                if (keywordFilter.expertQuery.filter?.length > 0) {
                    expertBooleanQuery.filter = `(${keywordFilter.expertQuery.filter}) AND (${filterQuery.expertBooleanQuery.filter})`;
                } else {
                    expertBooleanQuery.filter = `(${filterQuery.expertBooleanQuery.filter})`;
                }

            } else if (filterQuery.keywordGroups?.some(group => group.isAnyKeywordSet())) {
                // Saved filter basic && source filter expert
                if (keywordFilter.expertQuery.filter.length > 0) {
                    expertBooleanQuery.filter = `(${keywordFilter.expertQuery.filter})`;
                    // NOT only keyword groups will be converted into AND NOT (keywords)
                    // If we add AND to this case, the query will be AND AND NOT
                    if (filterQuery.hasPositiveKeywordGroups()) {
                        expertBooleanQuery.filter += ' AND ';
                    }
                    expertBooleanQuery.filter += `${expertBooleanQuery.convertBasicQueryToExpert(filterQuery.keywordGroups)}`;
                } else {
                    expertBooleanQuery.filter = expertBooleanQuery.convertBasicQueryToExpert(filterQuery.keywordGroups);
                }
            }
            // Saved filter expert && source filter basic
            if (keywordFilter.keywordGroups?.some(group => group.isAnyKeywordSet())) {
                const keywords = keywordFilter.expertQuery.convertBasicQueryToExpert(keywordFilter.keywordGroups);
                if (filterQuery.expertBooleanQuery.filter.length > 0) {
                    expertBooleanQuery.filter += ` AND ${keywords}`;
                } else {
                    expertBooleanQuery.filter = keywords;
                }
            }

            this.keywords.setFilterMode(KeywordFilterModeEnum.expert);
            this.keywords.setCurrentValue(
                {
                    keywordGroups: [],
                    expertQuery:  expertBooleanQuery
                }
            );
            // Saved filter basic && source filter basic
        } else if (filterQuery.keywordGroups?.some(group => group.isAnyKeywordSet())) {
            this.keywords.setFilterMode(KeywordFilterModeEnum.standard);
            const keywordGroups = [...this.keywords.getCurrentValue().keywordGroups, ...filterQuery.keywordGroups];
            this.keywords.setCurrentValue(
                {
                    keywordGroups: keywordGroups,
                    expertQuery:  new MonitoringAdvancedBooleanModel()
                }
            );
        }

        const authorsIncluded = filterQuery.includedSources
            .filter(source => source.entityType === SearchCriteriaSourcesEnum.journalist)
            .map(x => x.displayString);
        const authorsExcluded = filterQuery.excludedSources
            .filter(source => source.entityType === SearchCriteriaSourcesEnum.journalist)
            .map(x => x.displayString);
        if (this.authors.isInclude()) {
            this.authors.setCurrentValue(uniqBy([...this.authors.getCurrentValue(), ...authorsIncluded], 'id'));
        } else {
            this.authors.setCurrentValue(uniqBy([...this.authors.getCurrentValue(), ...authorsExcluded], 'id'));
        }


        const outletsIncluded = filterQuery.includedSources.filter(source => source.entityType === SearchCriteriaSourcesEnum.outlet).map(x=> x.displayString);
        const outletsExcluded = filterQuery.excludedSources.filter(source => source.entityType === SearchCriteriaSourcesEnum.outlet).map(x=> x.displayString);
        if (this.outlets.isInclude()) {
            this.outlets.setCurrentValue(uniqBy([...this.outlets.getCurrentValue(), ...outletsIncluded], 'id'));
        } else {
            this.outlets.setCurrentValue(uniqBy([...this.outlets.getCurrentValue(), ...outletsExcluded], 'id'));
        }


        const languagesIncluded = filterQuery.includedLanguages.map((language) => {
            return {
                label: language.name,
                value: language
            };
        });

        const languagesExcluded = filterQuery.excludedLanguages.map((language) => {
            return {
                label: language.name,
                value: language
            };
        });

        if (this.languages.isInclude()) {
            this.languages.options.push(...languagesIncluded);
            this.languages.setCurrentValue(uniqBy([...this.languages.getCurrentValue(), ...filterQuery.includedLanguages], 'id'));
        } else {
            this.languages.options.push(...languagesExcluded);
            this.languages.setCurrentValue(uniqBy([...this.languages.getCurrentValue(), ...filterQuery.excludedLanguages], 'id'));
        }

        if (this.mediaTypes.isInclude()) {
            this.mediaTypes.setCurrentValue(new SelectedMediaTypesFilterModel(
                uniqBy([...this.mediaTypes.getCurrentValue().mediaTypes, ...filterQuery.includedMediaTypes], 'id'), false)
            );
        } else {
            this.mediaTypes.setCurrentValue(new SelectedMediaTypesFilterModel(
                uniqBy([...this.mediaTypes.getCurrentValue().mediaTypes, ...filterQuery.excludedMediaTypes], 'id'), false)
            );
        }

        if (this.regions.isInclude() && filterQuery.rawIncludedRegions) {
            this.journodexService.mapToComplexRegionInfo(filterQuery.rawIncludedRegions).subscribe(
                (regions) => {
                    const includedRegions = regions.map(region => {
                        return {
                            label: region.displayString,
                            value: region
                        };
                    });
                    this.regions.setCurrentValue([...this.regions.getCurrentValue(), ...includedRegions]);
                }
            );
        } else if (filterQuery.rawExcludedRegions) {
            this.journodexService.mapToComplexRegionInfo(filterQuery.rawExcludedRegions).subscribe(
                (regions) => {
                    const excludedRegions = regions.map(region => {
                        return {
                            label: region.displayString,
                            value: region
                        };
                    });
                    this.regions.setCurrentValue([...this.regions.getCurrentValue(), ...excludedRegions]);
                }
            );
        }

        const newSentiments = [];
        uniqBy([...this.sentiment.getCurrentValue(), ...filterQuery.sentiments], 'id')?.forEach(val => {
            this.sentiment.options.forEach(option => {
                if (option.value.id === val.id) {
                    newSentiments.push(option.value);
                }
            });
        });

        this.sentiment.setCurrentValue(newSentiments);

        if (!this.audience.getCurrentValue().min && !this.audience.getCurrentValue().max) {
            this.audience.setCurrentValue(filterQuery.audienceReach);
        }
        if (!this.ave.getCurrentValue().min && !this.ave.getCurrentValue().max) {
            this.ave.setCurrentValue(filterQuery.ave);
        }

        const engagementCurrentValue = this.engagement.getCurrentValue();
        for(const [key, value] of Object.entries(engagementCurrentValue)) {
            if (value && !(isNil(value.min) || isNaN(value.min)) && !(isNil(value.max) || isNaN(value.max))) {
                engagementCurrentValue[key] = filterQuery.engagementFilter[key];
            }
        }
        this.engagement.setCurrentValue(engagementCurrentValue);

        if (!this.engagementRate.getCurrentValue().min && !this.engagementRate.getCurrentValue().max) {
            this.engagementRate.setCurrentValue(filterQuery.engagementFilter.engagementTotal);
        }
        if (!this.domainAuthority.getCurrentValue().min && !this.domainAuthority.getCurrentValue().max) {
            this.domainAuthority.setCurrentValue(filterQuery.domainAuthority);
        }

        if (this.tags.isInclude()) {
            this.tags.setCurrentValue([...new Set([...this.tags.getCurrentValue(), ...filterQuery.tag.include])]);
        } else {
            this.tags.setCurrentValue([...new Set([...this.tags.getCurrentValue(), ...filterQuery.tag.exclude])]);
        }

        if (this.tagGroups.isInclude()) {
            this.tagGroups.setCurrentValue([...new Set([...this.tagGroups.getCurrentValue(), ...filterQuery.tagGroup.include])]);
        } else {
            this.tagGroups.setCurrentValue([...new Set([...this.tagGroups.getCurrentValue(), ...filterQuery.tagGroup.exclude])]);
        }

        if (this.hashtags.isInclude()) {
            this.hashtags.setCurrentValue([...new Set([...this.hashtags.getCurrentValue(), ...filterQuery.hashtags.include])]);
        } else  {
            this.hashtags.setCurrentValue([...new Set([...this.hashtags.getCurrentValue(), ...filterQuery.hashtags.include])]);
        }

        if (this.sourceGroups.isInclude()) {
            this.sourceGroups.setCurrentValue([...new Set([...this.sourceGroups.getCurrentValue(), ...filterQuery.sourceGroup.include])]);
        } else {
            this.sourceGroups.setCurrentValue([...new Set([...this.sourceGroups.getCurrentValue(), ...filterQuery.sourceGroup.include])]);
        }

        if (filterQuery.discoverListFilter) {
            this.discoverListsFilterService.populateFilter(this.discoverLists, filterQuery.discoverListFilter, true).subscribe();
        }

        if (AgentsFilterModel.hasQuery(filterQuery)) {
            this.aiAgents.addCurrentFromQuery(filterQuery);
        }
    }

    prepareFilterModelToSave(): SavedFilter {
        const filterToSave = new SavedFilter();
        filterToSave.type = SavedFilterTypeEnum.monitoring;
        filterToSave.query = this.generateFilterQuery();
        if (this.filters.includes(this.duplicates)) {
            filterToSave.showSimilar = this.duplicates.getCurrentValue() !== DuplicatesStatusEnum.hide;
        };
        return filterToSave;
    }

    constructFilterCardFromValue(val: SavedFilter): SavedFilterCard {
        const query = val.query;
        const filters: SavedFilterDisplayModel[] = [];
        if (query.searchMode === SearchMode.expert) {
            filters.push({
                filterName: 'Monitoring.filtersLocalization.keywords',
                type: monitoringFiltersEnum.keywords,
                chips: [{
                    label: 'Expert Query',
                    tooltip: query.expertBooleanQuery?.filter,
                    labelArgs: { searchContexts: query.expertBooleanQuery?.searchContexts?.map(x => x.value) }
                }]
            });
        } else {
            const keywordFilter = new MonitoringKeywordFilterModel(null, null, null, this.localizationService);
            keywordFilter.setValue(    {
                keywordGroups: query.keywordGroups,
                expertQuery: new MonitoringAdvancedBooleanModel()
            });

            filters.push({
                filterName: 'Monitoring.filtersLocalization.keywords',
                type: monitoringFiltersEnum.keywords,
                chips: keywordFilter.chips
            });
        }

        if (query.includedMediaTypes?.length) {
            filters.push({
                filterName: 'Monitoring.filtersLocalization.mediaTypes',
                type: monitoringFiltersEnum.mediaTypes,
                includeExclude: IncludeExcludeEnum.include,
                chips: query.includedMediaTypes?.map(x => {
                    return {label: x.name};
                })
            });
        } else if (query.excludedMediaTypes?.length) {
            filters.push({
                filterName: 'Monitoring.filtersLocalization.mediaTypes',
                type: monitoringFiltersEnum.mediaTypes,
                includeExclude: IncludeExcludeEnum.exclude,
                chips: query.excludedMediaTypes?.map(x => {
                    return {label: x.name};
                })
            });
        }

        const authorsIncluded = query.includedSources?.filter(source => source.entityType === SearchCriteriaSourcesEnum.journalist);
        const authorsExcluded = query.excludedSources?.filter(source => source.entityType === SearchCriteriaSourcesEnum.journalist);

        if (query.filterSources?.length > 0) {
            query.filterSources.forEach(source => {
                if (source.type === SourceTypeEnum.journalist) {
                    authorsIncluded.push({
                        // @HACK when constructing filterSources, we add a pair of double quotes for b/e - refer to D20061 NEP-8747
                        // Here we remove them to generate correct filter
                        entityName: source.value.name.slice(1, -1)
                    } as JournodexSourceModel);
                }
            });
        }

        if (authorsIncluded?.length) {
            filters.push({
                filterName: 'Monitoring.filtersLocalization.authors',
                type: monitoringFiltersEnum.authors,
                includeExclude: IncludeExcludeEnum.include,
                chips: authorsIncluded.map(author => {
                    const chip: ChipModel = {
                        label: author.entityName
                    };
                    return chip;
                })
            });
        } else if (authorsExcluded?.length) {
            filters.push({
                filterName: 'Monitoring.filtersLocalization.authors',
                type: monitoringFiltersEnum.authors,
                includeExclude: IncludeExcludeEnum.exclude,
                chips: authorsExcluded.map(author => {
                    const chip: ChipModel = {
                        label: author.entityName
                    };
                    return chip;
                })
            });
        }

        const outletsIncluded = query.includedSources?.filter(source => source.entityType === SearchCriteriaSourcesEnum.outlet);
        const outletsExcluded = query.excludedSources?.filter(source => source.entityType === SearchCriteriaSourcesEnum.outlet);
        if (query.filterSources?.length > 0) {
            query.filterSources.forEach(source => {
                if (source.type === SourceTypeEnum.outlet) {
                    outletsIncluded.push({
                        // @HACK when constructing filterSources, we add a pair of double quotes for b/e - refer to D20061 NEP-8747
                        // Here we remove them to generate correct filter
                        entityName: source.value.name.slice(1, -1)
                    } as JournodexSourceModel);
                }
            });
        }


        if (outletsIncluded?.length) {
            filters.push({
                filterName: 'Monitoring.filtersLocalization.outlets',
                type: monitoringFiltersEnum.outlets,
                includeExclude: IncludeExcludeEnum.include,
                chips: outletsIncluded.map(x => {
                    const chip: ChipModel = {
                        label: x.entityName
                    };
                    return chip;
                })
            });
        } else if (outletsExcluded?.length) {
            filters.push({
                filterName: 'Monitoring.filtersLocalization.outlets',
                type: monitoringFiltersEnum.outlets,
                includeExclude: IncludeExcludeEnum.exclude,
                chips: outletsExcluded.map(x => {
                    const chip: ChipModel = {
                        label: x.entityName
                    };
                    return chip;
                })
            });
        }

        const languagesIncluded = query.includedLanguages?.map((language) => {
            return {
                label: language.name,
                value: language
            };
        });

        const languagesExcluded = query.excludedLanguages?.map((language) => {
            return {
                label: language.name,
                value: language
            };
        });

        if (languagesIncluded?.length) {
            filters.push({
                filterName: 'Monitoring.filtersLocalization.languages',
                type: monitoringFiltersEnum.languages,
                includeExclude: IncludeExcludeEnum.include,
                chips: languagesIncluded.map(x => {
                    return {label: x.label};
                })
            });
        } else if (languagesExcluded?.length) {
            filters.push({
                filterName: 'Monitoring.filtersLocalization.languages',
                type: monitoringFiltersEnum.languages,
                includeExclude: IncludeExcludeEnum.exclude,
                chips: languagesExcluded.map(x => {
                    return {label: x.label};
                })
            });
        }

        if (query.rawIncludedRegions) {
            this.journodexService.mapToComplexRegionInfo(query.rawIncludedRegions).subscribe(
                (regions) => {
                    filters.push({
                        filterName:'Monitoring.filtersLocalization.regions',
                        type: monitoringFiltersEnum.regions,
                        includeExclude: IncludeExcludeEnum.include,
                        chips: regions.map(region => {
                            return { label: region.displayString };
                        })
                    });

                }
            );
        }

        if (query.rawExcludedRegions) {
            this.journodexService.mapToComplexRegionInfo(query.rawIncludedRegions).subscribe(
                (regions) => {
                    filters.push({
                        filterName:'Monitoring.filtersLocalization.regions',
                        type: monitoringFiltersEnum.regions,
                        includeExclude: IncludeExcludeEnum.exclude,
                        chips: regions.map(region => {
                            return { label: region.displayString };
                        })
                    });

                }
            );
        }

        if (query.audienceReach) {
            const rangeFilter = new FilterTypeRangeModel(null, 'Monitoring.filtersLocalization.audience');
            filters.push({
                filterName: 'Monitoring.filtersLocalization.audience',
                type: monitoringFiltersEnum.audience,
                chips: rangeFilter.makeChip(query.audienceReach) ?
                    [rangeFilter.makeChip(query.audienceReach)] : []
            });
        }

        if (query.sentiments?.length) {
            filters.push({
                filterName: 'Monitoring.filtersLocalization.sentiment',
                type: monitoringFiltersEnum.sentiment,
                chips: query.sentiments?.map(x => {
                    return {label: x.name};
                })
            });
        }

        if (query.ave) {
            const rangeFilter = new FilterTypeRangeModel(null, 'Monitoring.filtersLocalization.ave');
            const aveRange = {
                min: +this.currencyConversionService.convertCurrency(query.ave.min, false),
                max: +this.currencyConversionService.convertCurrency(query.ave.max, false)
            };
            filters.push({
                filterName: 'Monitoring.filtersLocalization.ave',
                type: monitoringFiltersEnum.ave,
                chips: rangeFilter.makeChip(aveRange) ?
                    [rangeFilter.makeChip(aveRange)] : []
            });

        }

        if (query.tag.include.length > 0) {
            const chipLabels = query.tag.include.map(id => {
                if (this.tags.options.find(tag => tag.value === id)) {
                    return {label:this.tags.options.find(tag => tag.value === id).label };
                }
            });
            filters.push({
                filterName: 'Monitoring.filtersLocalization.tags',
                type: monitoringFiltersEnum.tags,
                includeExclude: IncludeExcludeEnum.include,
                chips: chipLabels
            });
        } else if (query.tag.exclude.length > 0) {
            const chipLabels = query.tag.exclude.map(id => {
                if (this.tags.options.find(tag => tag.value === id)) {
                    return {label: this.tags.options.find(tag => tag.value === id).label };
                }
            });
            filters.push({
                filterName: 'Monitoring.filtersLocalization.tags',
                type: monitoringFiltersEnum.tags,
                includeExclude: IncludeExcludeEnum.exclude,
                chips: chipLabels
            });
        }

        if (query.tagGroup.include.length > 0) {
            const chipLabels = query.tagGroup.include.map(id => {
                if (this.tagGroups.options.find(tag => tag.value === id)) {
                    return {label: this.tagGroups.options.find(tag => tag.value === id).label };
                }
            });
            filters.push({
                filterName: 'Monitoring.filtersLocalization.tagGroups',
                type: monitoringFiltersEnum.tagGroups,
                includeExclude: IncludeExcludeEnum.include,
                chips: chipLabels
            });
        } else if (query.tagGroup.exclude.length > 0) {
            const chipLabels = query.tagGroup.exclude.map(id => {
                if (this.tagGroups.options.find(tag => tag.value === id)) {
                    return {label: this.tagGroups.options.find(tag => tag.value === id).label };
                }
            });
            filters.push({
                filterName: 'Monitoring.filtersLocalization.tagGroups',
                type: monitoringFiltersEnum.tagGroups,
                includeExclude: IncludeExcludeEnum.exclude,
                chips: chipLabels
            });
        }

        if (query.hashtags?.include?.length) {
            filters.push({
                filterName: 'Monitoring.filtersLocalization.hashtags',
                type: monitoringFiltersEnum.hashtags,
                includeExclude: IncludeExcludeEnum.include,
                chips:  query.hashtags.include.map(x => {
                    return {label: x};
                })
            });

        } else if (query.hashtags?.exclude?.length) {
            filters.push({
                filterName: 'Monitoring.filtersLocalization.hashtags',
                type: monitoringFiltersEnum.hashtags,
                includeExclude: IncludeExcludeEnum.include,
                chips:  query.hashtags.exclude.map(x => {
                    return {label: x};
                })
            });
        }

        if (query.sourceGroup?.include?.length > 0) {
            const chipLabels = query.sourceGroup.include.map(id => {
                if (this.sourceGroups.options.find(tag => tag.value === id)) {
                    return {label: this.sourceGroups.options.find(tag => tag.value === id).label };
                }
            });
            filters.push({
                filterName: 'Monitoring.filtersLocalization.sourceGroups',
                type: monitoringFiltersEnum.sourceGroups,
                includeExclude: IncludeExcludeEnum.include,
                chips: chipLabels
            });
        } else if (query.sourceGroup?.exclude?.length > 0) {
            const chipLabels = query.sourceGroup.include.map(id => {
                if (this.sourceGroups.options.find(tag => tag.value === id)) {
                    return {label: this.sourceGroups.options.find(tag => tag.value === id).label };
                }
            });
            filters.push({
                filterName: 'Monitoring.filtersLocalization.sourceGroups',
                type: monitoringFiltersEnum.sourceGroups,
                includeExclude: IncludeExcludeEnum.include,
                chips: chipLabels
            });
        }

        if (query.engagementFilter?.engagementRate) {
            const rangeFilter = new FilterTypeRangeModel(null, 'Monitoring.filtersLocalization.engagementRate');
            rangeFilter.percentType = true;
            filters.push({
                filterName: 'Monitoring.filtersLocalization.engagementRate',
                type: monitoringFiltersEnum.engagementRate,
                chips: rangeFilter.makeChip(query.engagementFilter.engagementRate) ?
                    [rangeFilter.makeChip(query.engagementFilter.engagementRate)] : []
            });
        }

        if (query.engagementFilter?.engagementTotal) {
            const rangeFilter = new FilterTypeRangeModel(null, 'Monitoring.filtersLocalization.engagements');
            filters.push({
                filterName: 'Monitoring.filtersLocalization.engagements',
                type: monitoringFiltersEnum.engagementTotal,
                chips: rangeFilter.makeChip(query.engagementFilter.engagementTotal) ?
                    [rangeFilter.makeChip(query.engagementFilter.engagementTotal)] : []
            });
        }

        if (AgentsFilterModel.hasQuery(query)) {
            const { chips, includeExclude } = this.aiAgents.chipsForQuery(query);
            filters.push({
                filterName: 'Monitoring.filtersLocalization.aiAgents',
                type: monitoringFiltersEnum.aiAgent,
                includeExclude,
                chips
            });
        }

        if(query.discoverListFilter) {
            this.discoverListsFilterService.constructFilterCard(query.discoverListFilter).subscribe(
                savedFilter => filters.push(savedFilter)
            );
        }

        filters.push({
            filterName: 'Monitoring.filtersLocalization.duplicates',
            type: monitoringFiltersEnum.duplicates,
            chips: val.showSimilar === false ?
                [{
                    label: 'Monitoring.filtersLocalization.duplicatesHidden'
                }] : []
        });



        //  Sort the filter card to the same order as filter list
        let positionToAdd = 0;
        this.filters.forEach(_filter => {
            if (filters.findIndex(x => x.type === _filter.id) >= 0) {
                const matchedFilter = filters.splice(filters.findIndex(x => x.type === _filter.id), 1)[0];
                matchedFilter.isCompatible = true;
                filters.splice(positionToAdd, 0, matchedFilter);
                positionToAdd++;
            }
        });

        const filterCard: SavedFilterCard = {
            label: val.name,
            value: val,
            filters: filters.filter(x => x.isCompatible && x.chips?.length) ?? [],
            inCompatibleFilters: filters.filter(x => !x.isCompatible && x.chips?.length) ?? []
        };
        return filterCard;
    }

    private checkForSavedFilterConflicts() {
        const rangeFilterTypes = [
            monitoringFiltersEnum.ave,
            monitoringFiltersEnum.audience,
            monitoringFiltersEnum.engagementRate,
            monitoringFiltersEnum.engagementTotal
        ];

        const errorMessage = 'Common.filter.savedFilters.conflictFilter.chipMessage';

        let hasSavedFilterConflicts = false;
        let rangeIsValid: boolean;

        this.savedFilters.filterCards.forEach(filterCard => {
            const savedFilterQuery = filterCard.value.query;
            let conflictingValues = [];

            filterCard.filters.forEach(savedFilter => {
                const matchedSourceFilter = this.filters.find(x => x.id === savedFilter.type);

                if (!matchedSourceFilter) {
                    return;
                }

                if (rangeFilterTypes.find(x => x === matchedSourceFilter.id)) {
                    // Check if range value is valid if filter is a range filter type
                    if (matchedSourceFilter.id === monitoringFiltersEnum.ave) {
                        rangeIsValid = MentionSearchQueryModel.validateRangeFilter(matchedSourceFilter.getCurrentValue(), savedFilterQuery.ave);
                    }
                    if (matchedSourceFilter.id === monitoringFiltersEnum.audience) {
                        rangeIsValid = MentionSearchQueryModel.validateRangeFilter(matchedSourceFilter.getCurrentValue(), savedFilterQuery.audienceReach);
                    }
                    if (matchedSourceFilter.id === monitoringFiltersEnum.engagementRate) {
                        rangeIsValid = MentionSearchQueryModel.validateRangeFilter(
                            matchedSourceFilter.getCurrentValue(),
                            savedFilterQuery.engagementFilter?.engagementRate
                        );
                    }
                    conflictingValues = matchedSourceFilter.currentChips.map(chip => `"${this.localizationService.transform(chip.label)}"`);
                    savedFilter.errorMessage = this.localizationService.transform(errorMessage, {filters: conflictingValues.join(', ')});
                    hasSavedFilterConflicts = !rangeIsValid;
                } else {
                    // Check conflicting logic if filter is a include/exclude type
                    // if both source filter and saved filter have same filter values but different include/exclude type, it is considered conflict
                    savedFilter.chips.forEach(chip => {
                        const matchedChip = matchedSourceFilter.id === monitoringFiltersEnum.languages
                            ? matchedSourceFilter.getValue().find(x => x.name.toLowerCase() === chip.label.toLocaleLowerCase())
                            : matchedSourceFilter.id === monitoringFiltersEnum.discoverLists
                                ? matchedSourceFilter.currentChips.find(x =>
                                    x.label + (x.labelArgs as any)?.list === chip.label + (chip.labelArgs as any)?.list
                                )
                                : matchedSourceFilter.currentChips.find(x => x.label.toLowerCase() === chip.label.toLowerCase());

                        if (matchedChip) {
                            conflictingValues.push(`"${this.localizationService.transform(matchedChip.label)}"`);
                        }

                        if (conflictingValues.length && matchedSourceFilter.currentChips?.length > 0
                            && (
                                (matchedSourceFilter.isInclude() && savedFilter.includeExclude === IncludeExcludeEnum.exclude)
                                || (!matchedSourceFilter.isInclude() && savedFilter.includeExclude === IncludeExcludeEnum.include)
                            )
                        ) {
                            savedFilter.errorMessage = this.localizationService.transform(errorMessage, {filters: conflictingValues.join(', ')});
                            hasSavedFilterConflicts = true;

                        }
                    });
                }

                if (hasSavedFilterConflicts) {
                    throw new Error('Saved Filters have conflicts');
                } else {
                    savedFilter.errorMessage = null;
                }
            });
        });
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
        this.subscriptions.length = 0;
        this.requests.forEach(sub => sub.unsubscribe());
        this.requests.length = 0;
    }
}
